import { z } from "zod";
import { AI_MODES, CHANNELS, CONVERSATION_STATUSES, DATE_PRESETS, FOLLOWUP_TYPES } from "../enums";
import type {
  AiMode,
  Channel,
  ConversationStatus,
  CustomerSource,
  FollowupType,
  MessageDirection,
  MessageSender,
  MessageStatus,
  MessageType,
  OrderStatus,
} from "../enums";
import type { Paginated } from "./common";

// Conversations, channels, WhatsApp templates, quick replies and the website widget (WP3). Request schemas are used
// by the API (validation) and the web app (forms); DTO interfaces describe what the API returns.

const oid = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");

/** Query string booleans ("true", "1") without the z.coerce.boolean trap ("false" would be true). */
const queryBool = z
  .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
  .transform((v) => v === true || v === "true" || v === "1");

// ---------------------------------------------------------------------------------------------------------------
// Inbox

export const INBOX_TABS = ["all", "unread", ...CHANNELS] as const;
export type InboxTab = (typeof INBOX_TABS)[number];

/** Filter dropdown of the list: Toutes les conversations, Non assignées, Mes conversations, En attente, Traitées, Favoris, En pause. */
export const INBOX_VIEWS = ["all", "unassigned", "mine", "pending", "resolved", "starred", "snoozed"] as const;
export type InboxView = (typeof INBOX_VIEWS)[number];

export const INBOX_VIEW_LABELS: Record<InboxView, string> = {
  all: "Toutes les conversations",
  unassigned: "Non assignées",
  mine: "Mes conversations",
  pending: "En attente",
  resolved: "Traitées",
  starred: "Favoris",
  snoozed: "En pause",
};

export const INBOX_SORTS = ["recent", "oldest", "unread", "lead_score"] as const;
export type InboxSort = (typeof INBOX_SORTS)[number];

export const INBOX_SORT_LABELS: Record<InboxSort, string> = {
  recent: "Plus récentes",
  oldest: "Plus anciennes",
  unread: "Non lues d'abord",
  lead_score: "Meilleurs prospects",
};

export const AI_MODE_LABELS: Record<AiMode, string> = {
  auto: "IA automatique",
  assist: "IA assistante",
  off: "IA désactivée",
};

export const inboxListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  tab: z.enum(INBOX_TABS).default("all"),
  view: z.enum(INBOX_VIEWS).default("all"),
  status: z.enum(CONVERSATION_STATUSES).optional(),
  assigned: z.union([z.enum(["me", "none"]), oid]).optional(),
  starred: queryBool.optional(),
  q: z.string().trim().max(120).optional(),
  preset: z.enum(DATE_PRESETS).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  sort: z.enum(INBOX_SORTS).default("recent"),
});
export type InboxListQuery = z.infer<typeof inboxListQuery>;

export const inboxMessagesQuery = z.object({
  before: oid.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type InboxMessagesQuery = z.infer<typeof inboxMessagesQuery>;

export const inboxButtonSchema = z.object({
  id: z.string().trim().min(1, "Identifiant requis").max(256),
  title: z.string().trim().min(1, "Libellé requis").max(20, "20 caractères maximum"),
});

export const inboxSendSchema = z
  .object({
    text: z.string().max(4096, "Message trop long (4 096 caractères maximum)").optional().default(""),
    /** Files uploaded through /api/files. */
    attachments: z.array(z.object({ fileId: oid })).max(10, "10 pièces jointes maximum").optional().default([]),
    /** Approved WhatsApp template (required outside the 24 hour window). Values follow the template variables. */
    template: z
      .object({ templateId: oid, variables: z.array(z.string().max(1024)).max(20).optional().default([]) })
      .optional(),
    buttons: z.array(inboxButtonSchema).max(3, "3 boutons maximum").optional().default([]),
  })
  .refine((v) => v.text.trim().length > 0 || v.attachments.length > 0 || Boolean(v.template), {
    message: "Le message est vide",
    path: ["text"],
  });
export type InboxSendInput = z.infer<typeof inboxSendSchema>;

export const inboxNoteSchema = z.object({ text: z.string().trim().min(1, "Note vide").max(4000) });
export const inboxSnoozeSchema = z.object({ until: z.string().datetime({ offset: true }) });
export const inboxAssignSchema = z.object({ userId: oid.nullable() });
export const inboxStatusSchema = z.object({ status: z.enum(["open", "pending", "resolved"]) });
export const inboxTagsSchema = z.object({ tags: z.array(z.string().trim().min(1).max(40, "40 caractères maximum")).max(20) });
export const inboxAiModeSchema = z.object({ aiMode: z.enum(AI_MODES) });
export const inboxStarSchema = z.object({ starred: z.boolean() });
export const inboxBlockSchema = z.object({ blocked: z.boolean(), reason: z.string().trim().max(300).optional().default("") });
export const inboxCustomerNoteSchema = z.object({ text: z.string().trim().min(1, "Note vide").max(4000) });

export const inboxCustomerUpdateSchema = z.object({
  name: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(30).optional(),
  email: z.union([z.literal(""), z.string().trim().toLowerCase().email("Adresse e-mail invalide").max(254)]).optional(),
  governorate: z.string().trim().max(60).optional(),
  city: z.string().trim().max(80).optional(),
  address: z.string().trim().max(240).optional(),
});
export type InboxCustomerUpdate = z.infer<typeof inboxCustomerUpdateSchema>;

export const inboxNewConversationSchema = z.object({
  channelAccountId: oid,
  phone: z.string().trim().min(6, "Numéro invalide").max(30),
  name: z.string().trim().max(120).optional().default(""),
  templateId: oid,
  variables: z.array(z.string().max(1024)).max(20).optional().default([]),
});
export type InboxNewConversationInput = z.infer<typeof inboxNewConversationSchema>;

export interface InboxCounts {
  all: number;
  unread: number;
  whatsapp: number;
  messenger: number;
  instagram: number;
  website: number;
}

export interface ConversationListItemDto {
  id: string;
  channel: Channel;
  channelAccountId: string;
  channelAccountName: string;
  customerId: string | null;
  customerName: string;
  customerAvatarUrl: string | null;
  status: ConversationStatus;
  assignedTo: { id: string; name: string } | null;
  starred: boolean;
  unreadCount: number;
  lastMessageAt: string;
  lastMessagePreview: string;
  lastMessageDirection: MessageDirection | null;
  snoozedUntil: string | null;
  tags: string[];
  aiMode: AiMode;
  leadScore: number;
  windowOpen: boolean;
  windowExpiresAt: string | null;
}

export interface InboxListResponse extends Paginated<ConversationListItemDto> {
  counts: InboxCounts;
}

export interface ConversationDetailDto extends ConversationListItemDto {
  contactPhone: string | null;
  lastInboundAt: string | null;
  createdAt: string;
  firstResponseSeconds: number | null;
  aiState: {
    stage: string;
    handoffReason: string | null;
    summary: string;
    orderId: string | null;
    lastRunAt: string | null;
    lastError: string | null;
  };
  leadScoreDetail: { score: number; intent: string | null; factors: string[] };
  referral: { source: string | null; adId: string | null; ctwaClid: string | null; headline: string | null; sourceUrl: string | null };
  channelAccount: { id: string; name: string; status: string; phoneDisplay: string | null; username: string; humanAgent: boolean };
  /** Free form messages are allowed right now (inside the channel window, or Human Agent tag for agents). */
  canSendFreeform: boolean;
  /** Templates are the only option (WhatsApp outside the window). */
  templateRequired: boolean;
  customerBlocked: boolean;
}

export interface InboxAttachmentDto {
  type: "image" | "video" | "audio" | "file" | "sticker" | "location";
  url: string | null;
  mime: string | null;
  name: string | null;
  size: number | null;
  thumbnailUrl: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface InboxMessageDto {
  id: string;
  conversationId: string;
  channel: Channel;
  direction: MessageDirection;
  sender: MessageSender;
  senderName: string;
  senderUserId: string | null;
  type: MessageType;
  text: string;
  attachments: InboxAttachmentDto[];
  template: { name: string; language: string; variables: string[] } | null;
  buttons: Array<{ id: string; title: string }>;
  reply: { id: string; title: string } | null;
  status: MessageStatus;
  error: { code: string | null; message: string | null } | null;
  aiGenerated: boolean;
  createdAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
}

export interface ConversationThreadDto {
  conversation: ConversationDetailDto;
  messages: InboxMessageDto[];
  hasMore: boolean;
}

export interface InboxSendResultDto {
  sent: boolean;
  reason?: string;
  messageId?: string;
  message?: InboxMessageDto;
}

export interface ConversationPanelDto {
  customer: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    governorate: string;
    city: string;
    address: string;
    language: string;
    source: CustomerSource;
    tags: string[];
    avatarUrl: string | null;
    blocked: boolean;
    createdAt: string;
    stats: { ordersCount: number; deliveredCount: number; returnedCount: number; totalSpentMinor: number };
  } | null;
  orders: {
    total: number;
    items: Array<{
      id: string;
      number: string;
      status: OrderStatus;
      totalMinor: number;
      currency: string;
      createdAt: string;
      itemsCount: number;
      firstItemName: string;
      firstItemImage: string | null;
    }>;
  };
  viewedProducts: Array<{ id: string; name: string; priceMinor: number; currency: string; imageUrl: string | null }>;
  notes: { total: number; items: Array<{ id: string; text: string; authorName: string; createdAt: string }> };
}

export interface InboxAssigneeDto {
  id: string;
  name: string;
  email: string;
  role: string;
}

// ---------------------------------------------------------------------------------------------------------------
// WhatsApp templates and quick replies

export const WA_TEMPLATE_CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"] as const;
export type WaTemplateCategory = (typeof WA_TEMPLATE_CATEGORIES)[number];

export const WA_TEMPLATE_CATEGORY_LABELS: Record<WaTemplateCategory, string> = {
  MARKETING: "Marketing",
  UTILITY: "Utilitaire",
  AUTHENTICATION: "Authentification",
};

export const TEMPLATE_PURPOSES = [...FOLLOWUP_TYPES, "general"] as const;
export type TemplatePurpose = FollowupType | "general";

/** WhatsApp review statuses (webhook message_template_status_update), shown as pills. */
export const WA_TEMPLATE_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Brouillon",
  PENDING: "En revue",
  APPROVED: "Approuvé",
  REJECTED: "Refusé",
  PAUSED: "En pause",
  DISABLED: "Désactivé",
  FLAGGED: "Signalé",
  IN_APPEAL: "En appel",
  REINSTATED: "Rétabli",
  PENDING_DELETION: "Suppression en cours",
  DELETED: "Supprimé",
  ARCHIVED: "Archivé",
  LIMIT_EXCEEDED: "Limite atteinte",
  LOCKED: "Verrouillé",
};

export const waTemplateButtonSchema = z
  .object({
    type: z.enum(["QUICK_REPLY", "URL", "PHONE_NUMBER"]),
    text: z.string().trim().min(1, "Libellé requis").max(25, "25 caractères maximum"),
    url: z.string().trim().url("URL invalide").max(2000).optional(),
    phone: z.string().trim().max(20).optional(),
  })
  .refine((b) => b.type !== "URL" || Boolean(b.url), { message: "URL requise", path: ["url"] })
  .refine((b) => b.type !== "PHONE_NUMBER" || Boolean(b.phone), { message: "Numéro requis", path: ["phone"] });

export const messageTemplateInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Nom requis")
    .max(512)
    .regex(/^[a-z0-9_]+$/, "Lettres minuscules, chiffres et _ uniquement"),
  channel: z.enum(["whatsapp", "any"]).optional().default("whatsapp"),
  purpose: z.enum(TEMPLATE_PURPOSES).optional().default("general"),
  language: z.string().trim().min(2).max(10).optional().default("fr"),
  category: z.enum(WA_TEMPLATE_CATEGORIES).optional().default("UTILITY"),
  header: z.string().trim().max(60, "60 caractères maximum").optional().default(""),
  body: z.string().trim().min(1, "Texte requis").max(1024, "1 024 caractères maximum"),
  footer: z.string().trim().max(60, "60 caractères maximum").optional().default(""),
  buttons: z.array(waTemplateButtonSchema).max(10, "10 boutons maximum").optional().default([]),
  /** Example values per variable key ("body.first_name"), required by Meta's review. */
  variableExamples: z.record(z.string().max(120), z.string().max(200)).optional().default({}),
  active: z.boolean().optional().default(true),
});
export type MessageTemplateInput = z.infer<typeof messageTemplateInputSchema>;

export const messageTemplateUpdateSchema = messageTemplateInputSchema.partial();
export type MessageTemplateUpdate = z.infer<typeof messageTemplateUpdateSchema>;

export const messageTemplateListQuery = z.object({
  channel: z.enum(["whatsapp", "any"]).optional(),
  purpose: z.enum(TEMPLATE_PURPOSES).optional(),
  approvedOnly: queryBool.optional(),
  q: z.string().trim().max(120).optional(),
});

export interface MessageTemplateDto {
  id: string;
  name: string;
  channel: "whatsapp" | "any";
  purpose: TemplatePurpose;
  language: string;
  category: WaTemplateCategory;
  header: string;
  body: string;
  footer: string;
  buttons: Array<{ type: string; text: string; url: string | null; phone: string | null }>;
  /** Qualified variable keys in send order: "header.order_ref", "body.1", "button.0". */
  variables: string[];
  variableExamples: string[];
  whatsapp: { status: string | null; externalId: string | null; wabaId: string | null; rejectedReason: string | null; syncedAt: string | null };
  /** Can be sent outside the 24 hour window right now. */
  sendable: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const quickReplyInputSchema = z.object({
  shortcut: z
    .string()
    .trim()
    .min(1, "Raccourci requis")
    .max(30)
    .regex(/^[a-zA-Z0-9_-]+$/, "Lettres, chiffres, - et _ uniquement"),
  title: z.string().trim().min(1, "Titre requis").max(80),
  body: z.string().trim().min(1, "Texte requis").max(2000),
});
export type QuickReplyInput = z.infer<typeof quickReplyInputSchema>;

export interface QuickReplyDto {
  id: string;
  shortcut: string;
  title: string;
  body: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------------------------------------------
// Channels

export const channelSettingsSchema = z.object({
  name: z.string().trim().min(1, "Nom requis").max(80).optional(),
  autoReply: z.boolean().optional(),
  greeting: z.string().trim().max(300).optional(),
  awayMessage: z.string().trim().max(500).optional(),
  humanAgent: z.boolean().optional(),
  widgetColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Couleur invalide (format #RRGGBB)")
    .optional(),
  widgetPosition: z.enum(["right", "left"]).optional(),
});
export type ChannelSettingsInput = z.infer<typeof channelSettingsSchema>;

export const whatsappManualSetupSchema = z.object({
  phoneNumberId: z.string().trim().regex(/^\d{5,25}$/, "Identifiant du numéro invalide (chiffres uniquement)"),
  wabaId: z.string().trim().regex(/^\d{5,25}$/, "Identifiant du compte WhatsApp Business invalide (chiffres uniquement)"),
  accessToken: z.string().trim().min(20, "Jeton d'accès invalide").max(1000),
  pin: z
    .union([z.literal(""), z.string().regex(/^\d{6}$/, "Le code PIN contient 6 chiffres")])
    .optional()
    .default(""),
});
export type WhatsappManualSetupInput = z.infer<typeof whatsappManualSetupSchema>;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (HH:MM)");

export const autoReplySettingsSchema = z.object({
  enabled: z.boolean().optional(),
  channels: z.array(z.enum(CHANNELS)).optional(),
  mode: z.enum(["auto", "assist"]).optional(),
  delaySeconds: z.number().int().min(0).max(600).optional(),
  maxRepliesPerHour: z.number().int().min(1).max(500).optional(),
  businessHoursOnly: z.boolean().optional(),
  businessHours: z
    .object({
      timezone: z.string().trim().min(3).max(60),
      days: z.array(z.number().int().min(0).max(6)).max(7),
      start: hhmm,
      end: hhmm,
    })
    .optional(),
  handoffKeywords: z.array(z.string().trim().min(2).max(40)).max(40).optional(),
});
export type AutoReplySettingsInput = z.infer<typeof autoReplySettingsSchema>;

export interface AutoReplySettingsDto {
  enabled: boolean;
  channels: Channel[];
  mode: "auto" | "assist";
  delaySeconds: number;
  maxRepliesPerHour: number;
  businessHoursOnly: boolean;
  businessHours: { timezone: string; days: number[]; start: string; end: string };
  handoffKeywords: string[];
}

export interface ChannelAccountDto {
  id: string;
  channel: Channel;
  provider: "meta" | "website";
  name: string;
  username: string;
  pictureUrl: string | null;
  phoneDisplay: string | null;
  externalId: string;
  wabaId: string | null;
  pageId: string | null;
  status: "active" | "error" | "disabled";
  lastError: string | null;
  qualityRating: string | null;
  subscribedAt: string | null;
  connectedVia: "oauth" | "manual" | "widget";
  hasToken: boolean;
  settings: {
    autoReply: boolean;
    greeting: string;
    awayMessage: string;
    humanAgent: boolean;
    widgetColor: string;
    widgetPosition: "right" | "left";
  };
  createdAt: string;
}

export interface ChannelsOverviewDto {
  items: ChannelAccountDto[];
  autoReply: AutoReplySettingsDto;
  widget: { key: string; scriptUrl: string; snippet: string; accountId: string | null; enabled: boolean };
  metaConfigured: boolean;
  webhookUrl: string;
}

// ---------------------------------------------------------------------------------------------------------------
// Website chat widget (public, no cookies)

export const widgetSessionSchema = z.object({
  widgetKey: z.string().trim().min(8).max(100),
  visitorId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, "Visiteur invalide"),
  pageUrl: z.string().max(2000).optional(),
});

export const widgetMessageSchema = z.object({ text: z.string().trim().min(1, "Message vide").max(2000, "Message trop long") });

export const widgetPollQuery = z.object({
  since: z.string().datetime({ offset: true }).optional(),
  seen: queryBool.optional(),
});
